// LLM generated - Model Claude Opus 4.5
/**
 * ModalFieldGroup Component
 *
 * This component receives field definitions and values via props from StaticFieldComponent.
 * It computes display labels from field definitions. No label propagation
 * from children is needed - the parent owns all display logic.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Flex, Stack, createStyles, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconLock } from '@tabler/icons-react';
import get from 'lodash.get';

import dt from '~/testing';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { StaticFieldProps } from '../../types/common';

import type { ModalFieldGroup } from './types';
import { getFirstChildError } from './utils/getFirstChildError';

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: 48,
    height: 'auto',
    width: '100%',
    paddingLeft: theme.other.spacing._12,
    paddingRight: theme.other.spacing._12,
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    textAlign: 'left',
  },
  wrapper: {
    cursor: 'pointer',
    position: 'relative',
  },
  error: {
    borderColor: theme.colors.red[6],
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[4],
  },
  tooltip: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'not-allowed',
  },
  tooltipLabel: {
    color: theme.colors.gray2[9],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    border: `1px solid ${theme.colors.gray2[2]}`,
    borderRadius: theme.radius.sm,
  },
  drawerContent: {
    minHeight: '80vh',
  },
}));

function ModalFieldGroupTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

/**
 * Helper to get the display label for a field value by looking up options
 */
function getLabelFromFieldOptions(
  fieldData: { options?: Array<{ value: unknown; label: string; hideGroupLabel?: boolean }> },
  value: unknown
): { label: string; hideGroupLabel?: boolean } | null {
  if (!fieldData?.options || value === undefined) return null;
  const option = fieldData.options.find((opt) => String(opt.value) === String(value));
  if (!option) return null;
  return { label: option.label, hideGroupLabel: option.hideGroupLabel };
}

export function ModalFieldGroupElement({
  field,
  errors,
  sectionKey,
  isTouched,
  allErrors,
  allValues,
  path = [],
  FieldSwitch,
  getFieldByKey,
  getFieldQueryData,
  setFieldValue,
}: StaticFieldProps<ModalFieldGroup>) {
  const [isOpen, setIsOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const { cx, classes } = useStyles();
  const theme = useMantineTheme();

  // Get first child error message using utility function
  // Only compute when touched to avoid unnecessary work
  const childErrorMessage = useMemo(
    () => (isTouched ? getFirstChildError(allErrors, sectionKey, path) : undefined),
    [allErrors, sectionKey, path, isTouched]
  );

  const isError = isTouched && ((errors && errors.length > 0) || !!childErrorMessage);

  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Track type field values to detect changes and clear non-type fields
  const typeFieldValuesRef = useRef<Record<string, unknown>>({});

  /**
   * Collect child field info: values and their display labels
   * This replaces the old label propagation system - we compute everything here
   * Uses props (getFieldByKey, getFieldQueryData, allValues) instead of context
   */
  const childFieldInfo = useMemo(() => {
    // If the lookup functions aren't provided, return empty array
    // This maintains backwards compatibility
    if (!getFieldByKey || !getFieldQueryData) {
      return [];
    }

    const info: Array<{
      key: string;
      value: unknown;
      label: string | null;
      hideGroupLabel?: boolean;
      isTypeField: boolean;
    }> = [];

    field.fields.forEach((childFieldKey) => {
      const childField = getFieldByKey(childFieldKey);
      if (!childField) return;

      const childFieldQuery = getFieldQueryData(childField);
      if (!childFieldQuery?.data) return;

      // Handle DynamicFields - need to recursively get child fields
      if (childFieldQuery.type === 'DynamicFields' && Array.isArray(childFieldQuery.data)) {
        childFieldQuery.data.forEach((nestedFieldKey: string) => {
          const nestedField = getFieldByKey(nestedFieldKey);
          if (!nestedField) return;

          const nestedFieldQuery = getFieldQueryData(nestedField);
          if (!nestedFieldQuery?.data || nestedFieldQuery.type === 'DynamicFields') return;

          const fieldData = nestedFieldQuery.data as {
            key: string;
            options?: Array<{ value: unknown; label: string; hideGroupLabel?: boolean }>;
            skipAutoClose?: boolean;
          };
          const fieldKey = fieldData.key;
          const childValue = get(allValues, [sectionKey, ...path, fieldKey]);
          // Use skipAutoClose - this is explicitly set by backend
          const isTypeField = fieldData.skipAutoClose === true;

          const labelInfo = getLabelFromFieldOptions(fieldData, childValue);

          info.push({
            key: fieldKey,
            value: childValue,
            label: labelInfo?.label || null,
            hideGroupLabel: labelInfo?.hideGroupLabel,
            isTypeField,
          });
        });
        return;
      }

      // At this point, fieldQuery.type is not 'DynamicFields', so data is StaticField
      const fieldData = childFieldQuery.data as {
        key: string;
        options?: Array<{ value: unknown; label: string; hideGroupLabel?: boolean }>;
        skipAutoClose?: boolean;
      };
      const fieldKey = fieldData.key;
      const childValue = get(allValues, [sectionKey, ...path, fieldKey]);
      // Use skipAutoClose - this is explicitly set by backend
      const isTypeField = fieldData.skipAutoClose === true;

      const labelInfo = getLabelFromFieldOptions(fieldData, childValue);

      info.push({
        key: fieldKey,
        value: childValue,
        label: labelInfo?.label || null,
        hideGroupLabel: labelInfo?.hideGroupLabel,
        isTypeField,
      });
    });

    return info;
  }, [field.fields, path, sectionKey, getFieldByKey, getFieldQueryData, allValues]);

  /**
   * When a type field value changes, clear all non-type field values.
   * This ensures that when switching between types (e.g., season vs segment),
   * the dependent value field resets to its default.
   *
   * We also try to immediately set the defaultValue if available from the field
   * definition, to avoid the lag of waiting for RadioGroupField's useEffect.
   */
  useEffect(() => {
    if (!setFieldValue || !getFieldByKey || !getFieldQueryData || childFieldInfo.length === 0)
      return;

    // Get current type field values
    const currentTypeValues: Record<string, unknown> = {};
    const nonTypeFields: Array<{ key: string; fieldKey: string }> = [];

    // We need to find the actual field keys for non-type fields
    field.fields.forEach((childFieldKey) => {
      const childField = getFieldByKey(childFieldKey);
      if (!childField) return;

      const childFieldQuery = getFieldQueryData(childField);
      if (!childFieldQuery?.data) return;

      // Handle DynamicFields
      if (childFieldQuery.type === 'DynamicFields' && Array.isArray(childFieldQuery.data)) {
        childFieldQuery.data.forEach((nestedFieldKey: string) => {
          const nestedField = getFieldByKey(nestedFieldKey);
          if (!nestedField) return;

          const nestedFieldQuery = getFieldQueryData(nestedField);
          if (!nestedFieldQuery?.data || nestedFieldQuery.type === 'DynamicFields') return;

          const fieldData = nestedFieldQuery.data as {
            key: string;
            skipAutoClose?: boolean;
          };
          if (fieldData.skipAutoClose) {
            currentTypeValues[fieldData.key] = get(allValues, [sectionKey, ...path, fieldData.key]);
          } else {
            nonTypeFields.push({ key: fieldData.key, fieldKey: nestedFieldKey });
          }
        });
        return;
      }

      const fieldData = childFieldQuery.data as { key: string; skipAutoClose?: boolean };
      if (fieldData.skipAutoClose) {
        currentTypeValues[fieldData.key] = get(allValues, [sectionKey, ...path, fieldData.key]);
      } else {
        nonTypeFields.push({ key: fieldData.key, fieldKey: childFieldKey });
      }
    });

    // Check if any type field value changed (but only if we have previous values)
    const prevTypeValues = typeFieldValuesRef.current;
    const hasTypeValueChanged = Object.keys(currentTypeValues).some((key) => {
      const prevValue = prevTypeValues[key];
      const currValue = currentTypeValues[key];
      // Only consider it a change if we had a previous value (not initial mount)
      return prevValue !== undefined && prevValue !== currValue;
    });

    // Update ref with current values
    typeFieldValuesRef.current = currentTypeValues;

    // If a type field changed, reset non-type fields to their defaultValue
    if (hasTypeValueChanged) {
      nonTypeFields.forEach((nonTypeField) => {
        // Try to get the defaultValue from the field definition
        const fieldQuery = getFieldByKey(nonTypeField.fieldKey);
        const fieldQueryData = fieldQuery ? getFieldQueryData(fieldQuery) : null;
        const fieldData = fieldQueryData?.data as { defaultValue?: unknown } | undefined;
        const defaultValue = fieldData?.defaultValue;

        // Set to defaultValue if available, otherwise clear to undefined
        setFieldValue([...path, nonTypeField.key], defaultValue, { clearTouched: true });
      });
    }
  }, [
    childFieldInfo,
    path,
    setFieldValue,
    field.fields,
    getFieldByKey,
    getFieldQueryData,
    allValues,
    sectionKey,
  ]);

  /**
   * Handle click on a radio option inside the drawer.
   * Close modal only when clicking on an actual radio option (marked with data-radio-option).
   * RadioGroupField adds data-radio-option to each option's clickable area.
   */
  const handleDrawerClick = useCallback(
    (e: React.MouseEvent) => {
      if (!field.autoCloseOnSelect) return;

      const target = e.target as HTMLElement;

      // Only proceed if we clicked within a radio option (RadioGroupField marks these)
      const radioOption = target.closest('[data-radio-option]');
      if (!radioOption) return;

      // Find the RadioGroupField container which has data-field-key
      const fieldContainer = target.closest('[data-field-key]') as HTMLElement | null;
      if (!fieldContainer) return;

      const fieldKey = fieldContainer.dataset.fieldKey || '';

      // Check if this field should skip auto-close by looking up in childFieldInfo
      const childInfo = childFieldInfo.find((child) => child.key === fieldKey);
      const shouldSkipAutoClose = childInfo?.isTypeField === true;

      if (!shouldSkipAutoClose) {
        setIsOpen(false);
      }
    },
    [field.autoCloseOnSelect, childFieldInfo]
  );

  const displayText = useMemo(() => {
    const typeLabels: string[] = [];
    const valueLabels: string[] = [];
    let shouldHideTypeLabels = false;

    childFieldInfo.forEach((childInfo) => {
      if (!childInfo.label) return;

      if (childInfo.hideGroupLabel) {
        shouldHideTypeLabels = true;
      }

      if (childInfo.isTypeField) {
        // Convert "Per season" to "per season" for better sentence flow
        typeLabels.push(childInfo.label.toLowerCase());
      } else {
        valueLabels.push(childInfo.label);
      }
    });

    // Build display: value first, then type (if exists and not hidden)
    const allLabels = shouldHideTypeLabels
      ? [...valueLabels].filter(Boolean)
      : [...valueLabels, ...typeLabels].filter(Boolean);

    if (allLabels.length > 0) {
      return allLabels.join(' ');
    }
    return field.placeholder || 'Select';
  }, [childFieldInfo, field.placeholder]);

  const showError = isError;

  return (
    <div
      className={classes.wrapper}
      data-test-id={dt.contest.create.accordion}
      onBlur={() => setHover(false)}
      onFocus={() => setHover(!hover)}
      onClick={() => setHover(!hover)}
      onKeyDown={() => setHover(!hover)}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      role="button"
      tabIndex={0}
    >
      <Button
        variant="text"
        data-test-id={dt.contest.create.selectOption}
        disabled={field.disabled}
        onClick={handleOpenModal}
        className={cx(classes.card, { [classes.error]: showError })}
      >
        <Flex align="center" justify="space-between">
          <Stack spacing={0}>
            <Flex align="center" gap={theme.other.spacing._4}>
              {field.indicator && <div className={classes.indicator} />}
              <Text variant="body-small" c={theme.colors.gray[6]}>
                {field.label}
              </Text>
            </Flex>
            <Text variant="title-medium" c={showError ? theme.colors.red[6] : 'inherit'}>
              {showError ? errors?.[0] || childErrorMessage || displayText : displayText}
            </Text>
          </Stack>
          {field.locked ? (
            <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
          ) : (
            <IconChevronDown size={18} stroke={theme.colors.gray[4]} />
          )}
        </Flex>
        {field.locked && hover ? (
          <div className={classes.tooltip}>
            <Text className={classes.tooltipLabel} variant="body-medium">
              {field.locked}
            </Text>
          </div>
        ) : null}
      </Button>

      {/*
        Render children in a hidden container ALWAYS to ensure:
        1. Default values are set (especially for HiddenField)
        2. DynamicFields fetch their data
        3. Validation schemas are properly built
        When drawer is open, we render them again inside the Drawer for visibility
      */}
      <div style={{ display: 'none' }}>
        {field.fields.map((childFieldKey) => (
          <FieldSwitch
            key={`hidden-${childFieldKey}`}
            fieldKey={childFieldKey}
            sectionKey={sectionKey || ''}
            path={path}
          />
        ))}
      </div>

      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        title={<ModalFieldGroupTitle label={field.modalHeader || field.label} />}
        classes={{ content: classes.drawerContent }}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClick={handleDrawerClick}>
          <Stack spacing={theme.other.spacing._16} p="md">
            {field.fields.map((childFieldKey) => (
              <FieldSwitch
                key={childFieldKey}
                fieldKey={childFieldKey}
                sectionKey={sectionKey || ''}
                path={path}
              />
            ))}
          </Stack>
        </div>
      </Drawer>
    </div>
  );
}
